<template>
  <div class="property-panel" v-if="annotation">
    <div class="panel-header">
      <h4>Field Properties</h4>
      <button class="btn btn-sm btn-dc btn-danger" @click="deleteField">
        <i class="fa fa-trash"></i>
      </button>
    </div>

    <div class="property-group">
      <label class="property-label">Field Name</label>
      <input type="text" v-model="localAnnotation.fieldName" @blur="validateAndUpdate"
        class="form-control property-input" placeholder="Enter field name" />
    </div>

    <div class="property-group">
      <label class="property-label">Label</label>
      <input type="text" v-model="localAnnotation.label" @blur="updateAnnotation" class="form-control property-input"
        placeholder="Enter field label" />
    </div>

    <div class="property-group">
      <label class="checkbox-label">
        <input type="checkbox" v-model="localAnnotation.required" @change="updateAnnotation" />
        <span>Required field</span>
      </label>
    </div>

    <!-- Text field specific properties -->
    <div v-if="annotation.type === 'text'" class="property-group">
      <label class="checkbox-label">
        <input type="checkbox" v-model="localAnnotation.properties.multiline" @change="updateAnnotation" />
        <span>Multiline text</span>
      </label>
    </div>

    <div v-if="annotation.type === 'text'" class="property-group">
      <label class="property-label">Placeholder</label>
      <input type="text" v-model="localAnnotation.properties.placeholder" @blur="updateAnnotation"
        class="form-control property-input" placeholder="Enter placeholder text" />
    </div>

    <div v-if="annotation.type === 'text'" class="property-group">
      <label class="property-label">Max Length</label>
      <input type="number" v-model.number="localAnnotation.properties.maxLength" @blur="updateAnnotation"
        class="form-control property-input" min="1" placeholder="Maximum characters" />
    </div>

    <!-- Select field specific properties -->
    <div v-if="annotation.type === 'select'" class="property-group">
      <label class="property-label">Options</label>
      <div class="options-list">
        <div v-for="(option, index) in localAnnotation.properties.options" :key="index" class="option-item">
          <input type="text" v-model="option.label" @blur="updateAnnotation" class="form-control option-input"
            placeholder="Option label" />
          <input type="text" v-model="option.value" @blur="updateAnnotation" class="form-control option-input"
            placeholder="Option value" />
          <button class="btn btn-sm btn-danger" @click="removeOption(index)">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <button class="btn btn-sm btn-success" @click="addOption">
          <i class="fa fa-plus"></i> Add Option
        </button>
      </div>
    </div>

    <!-- Date field specific properties -->
    <div v-if="annotation.type === 'date'" class="property-group">
      <label class="property-label">Date Format</label>
      <select v-model="localAnnotation.properties.format" @change="updateAnnotation"
        class="form-control property-input">
        <option value="MM/DD/YYYY">MM/DD/YYYY</option>
        <option value="DD/MM/YYYY">DD/MM/YYYY</option>
        <option value="YYYY-MM-DD">YYYY-MM-DD</option>
      </select>
    </div>

    <div v-if="annotation.type === 'date'" class="property-group">
      <label class="property-label">Min Date</label>
      <input type="date" v-model="localAnnotation.properties.minDate" @change="updateAnnotation"
        class="form-control property-input" />
    </div>

    <div v-if="annotation.type === 'date'" class="property-group">
      <label class="property-label">Max Date</label>
      <input type="date" v-model="localAnnotation.properties.maxDate" @change="updateAnnotation"
        class="form-control property-input" />
    </div>

    <!-- Signature field specific properties -->
    <div v-if="annotation.type === 'signature'">
      <div class="property-group">
        <label class="property-label">Signee Type</label>
        <select v-model="localAnnotation.signee_type" @change="updateAnnotation"
          class="form-control property-input">
          <option value="">-- Select Signee Type --</option>
          <option value="student">Student</option>
          <option value="guardian">Guardian</option>
          <option value="instructor">Instructor</option>
          <option value="staff">Staff</option>
        </select>
        <small class="help-text">
          Determines which user role is required to sign this field.
        </small>
      </div>

      <div class="property-group">
        <label class="property-label">Box Style</label>
        <select v-model="localAnnotation.properties.boxStyle" @change="updateAnnotation"
          class="form-control property-input">
          <option value="border">Border</option>
          <option value="underline">Underline</option>
          <option value="none">None</option>
        </select>
      </div>

    </div>

    <!-- Position properties -->
    <div class="property-group">
      <label class="property-label">Position</label>
      <div class="position-inputs">
        <div class="position-row">
          <label>X:</label>
          <input type="number" v-model.number="localAnnotation.position.x" @blur="updateAnnotation"
            class="form-control position-input" />
          <label>Y:</label>
          <input type="number" v-model.number="localAnnotation.position.y" @blur="updateAnnotation"
            class="form-control position-input" />
        </div>
        <div class="position-row">
          <label>W:</label>
          <input type="number" v-model.number="localAnnotation.position.width" @blur="updateAnnotation"
            class="form-control position-input" min="50" />
          <label>H:</label>
          <input type="number" v-model.number="localAnnotation.position.height" @blur="updateAnnotation"
            class="form-control position-input" min="30" />
        </div>
      </div>
    </div>

    <!-- Assignable roles (hidden for signature fields as signee_type determines this) -->
    <div class="property-group" v-if="annotation.type !== 'signature'">
      <label class="property-label">Assignable To</label>
      <div class="assignee-options">
        <label class="checkbox-label">
          <input type="checkbox" value="student" v-model="localAnnotation.assignableTo" @change="updateAnnotation" />
          <span>Student</span>
        </label>
        <label class="checkbox-label">
          <input type="checkbox" value="guardian" v-model="localAnnotation.assignableTo" @change="updateAnnotation" />
          <span>Guardian</span>
        </label>
        <label class="checkbox-label">
          <input type="checkbox" value="instructor" v-model="localAnnotation.assignableTo" @change="updateAnnotation" />
          <span>Instructor</span>
        </label>
        <label class="checkbox-label">
          <input type="checkbox" value="admin" v-model="localAnnotation.assignableTo" @change="updateAnnotation" />
          <span>Admin</span>
        </label>
      </div>
      <small class="help-text">
        Select which user roles can fill out this field.
      </small>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';

export default {
  name: 'PropertyPanel',
  props: {
    annotation: {
      type: Object,
      default: null
    },
    allAnnotations: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      localAnnotation: null
    };
  },
  computed: {
    availableFields() {
      // Filter out signature fields and the current annotation
      // Only show text, select, and date fields that can be linked
      return this.allAnnotations.filter(ann => {
        return ann.id !== this.annotation?.id &&
          ['text', 'select', 'date'].includes(ann.type);
      });
    }
  },
  watch: {
    annotation: {
      handler(newAnnotation) {
        if (newAnnotation) {
          // Selective deep clone - only clone nested objects that will be mutated
          // This avoids the performance cost of JSON.parse(JSON.stringify())
          // and only runs when the annotation reference changes, not on every property change
          this.localAnnotation = {
            ...newAnnotation,
            position: { ...newAnnotation.position },
            properties: this.cloneProperties(newAnnotation.properties),
            assignableTo: [...(newAnnotation.assignableTo || [])],
            // Include signee_type at top level for signature fields
            signee_type: newAnnotation.signee_type || ''
          };
        } else {
          this.localAnnotation = null;
        }
      },
      immediate: true
      // Note: deep: true removed - we only want to react to reference changes
      // not every nested property mutation (which we ourselves are causing)
    }
  },
  methods: {
    cloneProperties(properties) {
      if (!properties) {
        return {};
      }

      const cloned = { ...properties };

      // Deep clone the options array for select fields
      // This is the only nested array that gets mutated
      if (cloned.options && Array.isArray(cloned.options)) {
        cloned.options = cloned.options.map(option => ({ ...option }));
      }

      return cloned;
    },

    updateAnnotation() {
      if (this.localAnnotation) {
        // For signature fields, sync assignableTo with signee_type
        if (this.localAnnotation.type === 'signature' && this.localAnnotation.signee_type) {
          // Update assignableTo array to match the selected signee_type
          this.localAnnotation.assignableTo = [this.localAnnotation.signee_type];

          // signee_type is only at top-level now, not duplicated in properties
        }

        this.$emit('update', this.localAnnotation);
      }
    },

    validateAndUpdate() {
      if (this.localAnnotation) {
        // Validate field name is not empty and is unique
        if (!this.localAnnotation.fieldName.trim()) {
          this.localAnnotation.fieldName = `field_${Date.now()}`;
        }
        this.$emit('validate-field-name', this.localAnnotation);
        this.updateAnnotation();
      }
    },

    deleteField() {
      if (this.annotation) {
        this.$emit('delete', this.annotation.id);
      }
    },

    addOption() {
      if (this.localAnnotation && this.localAnnotation.type === 'select') {
        this.localAnnotation.properties.options.push({
          label: '',
          value: ''
        });
        this.updateAnnotation();
      }
    },

    removeOption(index) {
      if (this.localAnnotation && this.localAnnotation.type === 'select') {
        this.localAnnotation.properties.options.splice(index, 1);
        this.updateAnnotation();
      }
    }
  }
};
</script>

<style scoped>
.property-panel {
  background-color: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
  max-height: 600px;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  /* Fix for height constraint issue in flex container */
  flex: 1 1 auto;
  min-height: 0; /* Allow flex item to shrink below content size */
  display: flex;
  flex-direction: column;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.panel-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
}

.property-group {
  margin-bottom: 16px;
}

.property-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #4a5568;
  margin-bottom: 6px;
}

.property-input {
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
}

.assignee-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.options-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.option-item {
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;
}

.option-input {
  flex: 1;
  font-size: 13px;
  min-width: 0;
  box-sizing: border-box;
}

.position-inputs {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.position-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.position-row label {
  font-size: 12px;
  font-weight: 500;
  min-width: 15px;
  flex-shrink: 0;
}

.position-input {
  flex: 1;
  font-size: 13px;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
}

.help-text {
  display: block;
  font-size: 12px;
  color: #718096;
  margin-top: 4px;
  font-style: italic;
}
</style>
